/* =============================================================================
 * Dica (tooltip) do atributo `title`.
 *
 * O app inteiro ja' escreve `title:` nos elementos - cabecalho de coluna que
 * abrevia o nome, botao so' de icone, item desabilitado explicando o motivo. O
 * problema e' o tooltip NATIVO que aparece a partir disso: ele demora cerca de
 * um segundo, some sozinho depois de alguns, ignora o tema do app, quebra a
 * linha onde quer e no Windows ainda vem com a fonte do sistema. Numa tela como
 * o calendario, onde o titulo e' a UNICA forma de ler "Novo Hamburgo x Sao
 * Jose" inteiro, isso e' informacao escondida atras de um componente que nao
 * controlamos.
 *
 * Aqui e' o mesmo contrato do iPrisma (Shell.RegisterTitles), portado para o
 * mundo sem jQuery: um par de listeners no document, o `title` movido para
 * `data-dica` no primeiro hover - e' ISSO que cala o nativo - e uma unica caixa
 * reaproveitada no body.
 *
 * UMA CAIXA SO', E NAO UMA POR ELEMENTO. O bug classico dessa familia e' o
 * tooltip orfao: a pagina re-renderiza com o mouse parado em cima, o mouseleave
 * nunca chega e sobra um retangulo flutuando na tela. Com no' unico + um laco
 * de rAF que confere `isConnected` enquanto esta' visivel, esse estado nao
 * existe: se o dono sumiu, a caixa some no proximo quadro.
 *
 * NAO INSTALA EM APARELHO DE TOQUE. Sem hover nao ha' gesto que abra a dica, e
 * o `title` nativo tambem nunca dispara ali - a informacao precisa estar na
 * tela, nao atras de um passar de mouse que nao existe.
 * ========================================================================== */

import { h } from '@shared/ui/dom.js';

/** Tempo parado sobre o elemento antes de abrir - evita a chuva de dicas de quem so' esta' atravessando a tela. */
const ATRASO_ABRIR = 450;
/** Trava de seguranca: fecha sozinha mesmo que o mouseleave nunca chegue. */
const TEMPO_MAXIMO_ABERTA = 6000;
/** Respiro entre a caixa e o elemento, e entre a caixa e a borda da janela. */
const DISTANCIA = 8;
const MARGEM = 8;

const ALVOS = '[title], [data-dica]';

let caixa = null;
let texto = null;
let alvoAtual = null;
let timerAbrir = null;
let timerFechar = null;
let quadro = 0;

/**
 * Liga a dica para o app inteiro. Chamada uma vez no boot, junto de
 * instalarLinks(): por delegacao, elemento criado depois ja' nasce coberto.
 */
export function instalarDicas() {
    //`hover: hover` sozinho mente em hibrido (notebook com tela sensivel
    //responde que sim); o `pointer: fine` e' quem confirma que existe um
    //ponteiro de verdade para parar em cima de alguma coisa.
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;

    //mouseenter/mouseleave nao sobem, entao a delegacao aqui e' com
    //mouseover/mouseout + closest - o mesmo que o jQuery faz por baixo.
    document.addEventListener('mouseover', aoEntrar);
    document.addEventListener('mouseout', aoSair);

    //Teclado tambem chega no botao de icone. `:focus-visible` mantem a dica
    //fora do caminho de quem chegou ali clicando.
    document.addEventListener('focusin', aoFocar);
    document.addEventListener('focusout', esconderDica);

    //Clique e Esc fecham: a dica nunca deve sobreviver a uma acao.
    document.addEventListener('pointerdown', esconderDica, true);
    document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape') esconderDica();
    });
}

/** Fecha o que estiver aberto. Exportada para quem troca a tela na mao. */
export function esconderDica() {
    clearTimeout(timerAbrir);
    clearTimeout(timerFechar);
    timerAbrir = null;
    timerFechar = null;
    cancelAnimationFrame(quadro);
    quadro = 0;
    alvoAtual = null;
    caixa?.classList.remove('dica--visivel');
}

function aoEntrar(ev) {
    const alvo = ev.target?.closest?.(ALVOS);
    if (!alvo) return;

    const conteudo = adotarTitulo(alvo);
    if (!conteudo) return;

    //Voltou para o mesmo elemento antes de fechar: cancela a saida e mantem.
    if (alvo === alvoAtual) {
        clearTimeout(timerFechar);
        timerFechar = null;
        return;
    }

    esconderDica();
    timerAbrir = setTimeout(() => {
        timerAbrir = null;
        //Entre agendar e abrir a pagina pode ter trocado, ou o mouse ja' pode
        //ter ido embora sem que o mouseout chegasse (elemento recriado).
        if (!alvo.isConnected || !alvo.matches(':hover')) return;
        mostrar(alvo, conteudo);
    }, ATRASO_ABRIR);
}

function aoSair(ev) {
    const alvo = ev.target?.closest?.(ALVOS);
    if (!alvo) return;
    //mouseout dispara ao andar entre filhos do mesmo elemento; so' e' saida de
    //verdade quando o destino esta' fora dele.
    if (alvo.contains(ev.relatedTarget)) return;

    clearTimeout(timerAbrir);
    timerAbrir = null;
    if (alvo === alvoAtual) esconderDica();
}

function aoFocar(ev) {
    const alvo = ev.target;
    if (!alvo?.matches?.(ALVOS)) return;
    const conteudo = adotarTitulo(alvo);
    if (!conteudo) return;
    try {
        if (!alvo.matches(':focus-visible')) return;
    } catch {
        return;
    }

    esconderDica();
    mostrar(alvo, conteudo);
}

/**
 * Tira o `title` do elemento e guarda em `data-dica`.
 *
 * E' o coracao do plugin: enquanto o atributo existir o navegador vai desenhar
 * o balao dele por cima do nosso. Roda sempre que o atributo reaparece, e nao
 * so' na primeira vez - assim `el.title = 'outro texto'` feito por uma tela e'
 * respeitado em vez de ficar preso no valor antigo.
 *
 * `data-sem-dica` e' a saida para quem quiser o comportamento nativo de volta.
 */
function adotarTitulo(alvo) {
    if (alvo.hasAttribute('data-sem-dica')) return '';

    const titulo = alvo.getAttribute('title');
    if (titulo !== null) {
        alvo.removeAttribute('title');
        const limpo = titulo.trim();
        if (!limpo) {
            alvo.removeAttribute('data-dica');
            return '';
        }
        alvo.setAttribute('data-dica', limpo);
        garantirNomeAcessivel(alvo, limpo);
    }
    return alvo.getAttribute('data-dica') || '';
}

/**
 * Em elemento SO' de icone o `title` era tambem o nome lido pelo leitor de
 * tela. Tirar o atributo sem repor deixaria um botao anonimo - entao quando nao
 * ha' nenhuma outra fonte de nome, o texto vira `aria-label`.
 *
 * Quando ja' existe nome (o proprio texto do elemento, ou um aria-label que a
 * tela escreveu de proposito) nada e' tocado: sobrescrever ali trocaria um nome
 * bom por um resumo.
 */
function garantirNomeAcessivel(alvo, titulo) {
    if (alvo.hasAttribute('aria-label') || alvo.hasAttribute('aria-labelledby')) return;
    if (alvo.textContent?.trim()) return;
    alvo.setAttribute('aria-label', titulo);
}

function mostrar(alvo, conteudo) {
    if (!caixa) criarCaixa();

    //Sem innerHTML, como em todo o resto do app: quebra de linha vira <br> de
    //verdade e o texto entra por textContent.
    texto.replaceChildren();
    conteudo.split('\n').forEach((linha, i) => {
        if (i > 0) texto.appendChild(h('br'));
        texto.appendChild(document.createTextNode(linha));
    });

    alvoAtual = alvo;
    caixa.classList.add('dica--visivel');
    posicionar();

    //Enquanto visivel, um quadro por vez confere se o dono ainda existe e
    //reacompanha rolagem/animacao. E' o que torna o tooltip orfao impossivel -
    //e sai de cena junto com a dica, sem listener permanente de scroll.
    const acompanhar = () => {
        if (!alvoAtual) return;
        if (!alvoAtual.isConnected) {
            esconderDica();
            return;
        }
        posicionar();
        quadro = requestAnimationFrame(acompanhar);
    };
    quadro = requestAnimationFrame(acompanhar);

    timerFechar = setTimeout(esconderDica, TEMPO_MAXIMO_ABERTA);
}

function criarCaixa() {
    texto = h('span', { class: 'dica__texto' });
    //`role=tooltip` sem aria-describedby de proposito: o nome acessivel ja' foi
    //resolvido em garantirNomeAcessivel, e apontar para ca' faria o leitor
    //anunciar o mesmo texto duas vezes.
    caixa = h('div', { class: 'dica', role: 'tooltip', 'aria-hidden': 'true' }, [texto, h('span', { class: 'dica__seta' })]);
    document.body.appendChild(caixa);
}

/**
 * Acima do elemento por padrao; abaixo quando nao cabe. O eixo horizontal e'
 * grudado no centro do alvo e depois preso dentro da janela - por isso a seta
 * tem posicao propria: com a caixa encostada na borda ela precisa continuar
 * apontando para o elemento, e nao para o meio de si mesma.
 */
function posicionar() {
    const alvo = alvoAtual;
    if (!alvo) return;

    const r = alvo.getBoundingClientRect();
    const c = caixa.getBoundingClientRect();
    const centro = r.left + r.width / 2;

    const forcada = alvo.getAttribute('data-dica-posicao');
    const cabeAcima = r.top - c.height - DISTANCIA >= MARGEM;
    const abaixo = forcada === 'baixo' || (forcada !== 'cima' && !cabeAcima);

    const topo = abaixo ? r.bottom + DISTANCIA : r.top - c.height - DISTANCIA;
    const esquerda = Math.min(Math.max(centro - c.width / 2, MARGEM), Math.max(MARGEM, window.innerWidth - c.width - MARGEM));

    caixa.classList.toggle('dica--baixo', abaixo);
    caixa.style.transform = `translate(${Math.round(esquerda)}px, ${Math.round(topo)}px)`;
    //Presa a no minimo 12px de cada ponta para nao escorregar do canto arredondado.
    caixa.style.setProperty('--seta-x', `${Math.round(Math.min(Math.max(centro - esquerda, 12), Math.max(12, c.width - 12)))}px`);
}
